import type {
  ConfiguratorConfiguratorRequestResourceConfiguratorRequestOutputJsonld,
  ConfiguratorConfiguratorRequestResourceConfiguratorRequestPatchInputMergePatchJson,
  ConfiguratorConfiguratorRequestResourceConfiguratorRequestPostInputJson,
  ConfiguratorRequestItemOutputJsonld,
} from "@/lib/api/generated/model";

/**
 * Domain type-alias seam for configurator requests, so the app depends on these
 * stable names rather than the generated client's verbose ones (per admin-v2's
 * data-layer convention).
 */
export type ConfiguratorRequest =
  ConfiguratorConfiguratorRequestResourceConfiguratorRequestOutputJsonld;

/**
 * One line of the request: the snapshot of a build, option or accessory the
 * driver picked. Titles and prices are copied by the backend at submit time, so
 * they stay correct even after the configurator changes.
 */
export type ConfiguratorRequestItem = ConfiguratorRequestItemOutputJsonld;

/**
 * Body accepted by the update-request endpoint. Only the status and the decline
 * reason can change: everything else is what the driver handed in.
 */
export type ConfiguratorRequestPatchInput =
  ConfiguratorConfiguratorRequestResourceConfiguratorRequestPatchInputMergePatchJson;

/** The states a request moves through, in the order an admin sees them. */
export const CONFIGURATOR_REQUEST_STATUSES = [
  "submitted",
  "approved",
  "waiting_offers",
  "offer_approved",
  "declined",
  "processed",
] as const;

export type ConfiguratorRequestStatus =
  (typeof CONFIGURATOR_REQUEST_STATUSES)[number];

/**
 * Where the request was put together. An internal one comes out of the
 * configurator and carries a conversion with the lines that were picked; an
 * external one was drawn up elsewhere and carries a description and a PDF
 * instead. Both walk the same status flow.
 */
export const CONFIGURATOR_TYPES = ["internal", "external"] as const;

export type ConfiguratorType = (typeof CONFIGURATOR_TYPES)[number];

/** A request that was put together outside the configurator. */
export function isExternalRequest(request: ConfiguratorRequest): boolean {
  return request.configuratorType === "external";
}

/**
 * Whether a charging station has to be arranged. Only asked for an electric
 * vehicle; "already_present" is deliberately not the same answer as "no".
 */
export const CHARGING_STATION_NEEDS = [
  "needed",
  "not_needed",
  "already_present",
] as const;

export type ChargingStationNeed = (typeof CHARGING_STATION_NEEDS)[number];

/**
 * One picked build or option, as handed in. Only the identity and the amount
 * are sent: the backend reads title, code and price from the source, so
 * anything else sent here is ignored.
 *
 * Typed here rather than taken from the generated client, which types the
 * request's `items` as `unknown[]`.
 */
export interface ConfiguratorRequestItemInput {
  source: string;
  amount: number;
}

/**
 * Body accepted by the create-request endpoint, with the items narrowed to what
 * they actually are.
 */
export type ConfiguratorRequestPostInput = Omit<
  ConfiguratorConfiguratorRequestResourceConfiguratorRequestPostInputJson,
  "items"
> & {
  items: ConfiguratorRequestItemInput[];
};
